import { useState, useEffect } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { apiFetch } from '../utils/api'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const colors = [
  '#4f46e5', // Indigo for total
  '#ef4444', // Red
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#84cc16', // Lime
  '#ec4899', // Pink
  '#6b7280', // Gray
]

// Builds the chart.js line data for the last 30 days: total usage plus one series per app.
const buildChartData = (stats) => {
  console.log('Processing chart data with stats:', stats)

  // Get last 30 days
  const today = new Date()
  const last30Days = []
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    last30Days.push(date.toISOString().split('T')[0])
  }

  console.log('Last 30 days:', last30Days)

  // Group stats by app_id and store app names
  const appStats = {}
  const appNames = {}
  const dailyTotals = {}

  stats.forEach(stat => {
    const appId = stat.app_id
    const appName = stat.app_name || 'Unknown App'
    const rawDate = stat.modified_date

    // Skip entries with empty or invalid dates
    if (!rawDate || rawDate === '') {
      console.log('Skipping stat with empty date:', stat)
      return
    }

    // For dates in format "2025-09-20" (no time part)
    const date = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate
    const count = stat.app_count

    console.log('Processing stat:', { appId, appName, rawDate, date, count })

    if (!appStats[appId]) {
      appStats[appId] = {}
      appNames[appId] = appName
    }
    appStats[appId][date] = count

    if (!dailyTotals[date]) {
      dailyTotals[date] = 0
    }
    dailyTotals[date] += count
  })

  console.log('Daily totals:', dailyTotals)
  console.log('App stats:', appStats)

  // Get unique app IDs
  const appIds = Object.keys(appStats)

  // Prepare datasets
  const datasets = []

  // Total line (always first)
  const totalData = last30Days.map(date => dailyTotals[date] || 0)
  datasets.push({
    label: 'Total',
    data: totalData,
    borderColor: colors[0],
    backgroundColor: colors[0] + '20',
    tension: 0.4,
    fill: false,
    pointRadius: 4,
    pointHoverRadius: 6,
  })

  // Individual app lines
  appIds.forEach((appId, index) => {
    const appData = last30Days.map(date => appStats[appId][date] || 0)
    const colorIndex = (index + 1) % colors.length
    const appName = appNames[appId]

    // Use app name instead of app ID
    datasets.push({
      label: appName,
      data: appData,
      borderColor: colors[colorIndex],
      backgroundColor: colors[colorIndex] + '20',
      tension: 0.4,
      fill: false,
      pointRadius: 3,
      pointHoverRadius: 5,
    })
  })

  // Format labels (show only every 5th day to avoid crowding)
  const labels = last30Days.map((date, index) => {
    if (index % 5 === 0 || index === last30Days.length - 1) {
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
    return ''
  })

  console.log('Final chart data:', { labels, datasets })

  return { labels, datasets }
}

const AppUsageChart = () => {
  const [chartData, setChartData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        setLoading(true)
        const response = await apiFetch('/api/admin/app-usage-stats')

        if (response.ok) {
          const data = await response.json()
          console.log('App usage stats received:', data)
          setChartData(buildChartData(data.stats || []))
        } else {
          console.error('Error response:', response.status, response.statusText)
          setError('Error loading app usage statistics')
        }
      } catch (err) {
        console.error('Error fetching usage stats:', err)
        setError('Network error loading statistics')
      } finally {
        setLoading(false)
      }
    }

    fetchUsageStats()
  }, [])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        position: 'top',
        labels: {
          color: '#e5e7eb',
          font: {
            size: 12
          },
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
        }
      },
      title: {
        display: true,
        text: 'App Usage Over Last 30 Days',
        color: '#e5e7eb',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#e5e7eb',
        bodyColor: '#e5e7eb',
        borderColor: '#4f46e5',
        borderWidth: 1,
        callbacks: {
          title: function(context) {
            const dataIndex = context[0].dataIndex
            const last30Days = []
            const today = new Date()
            for (let i = 29; i >= 0; i--) {
              const date = new Date(today)
              date.setDate(date.getDate() - i)
              last30Days.push(date.toISOString().split('T')[0])
            }
            const fullDate = last30Days[dataIndex]
            return new Date(fullDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} calls`
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date',
          color: '#9ca3af'
        },
        ticks: {
          color: '#9ca3af',
          maxRotation: 45
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Number of Calls',
          color: '#9ca3af'
        },
        ticks: {
          color: '#9ca3af',
          beginAtZero: true,
          stepSize: 1
        },
        min: 0,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      point: {
        hoverBackgroundColor: '#ffffff'
      }
    }
  }

  if (loading) {
    return (
      <div className="app-usage-chart">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <span>Loading app usage statistics...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="app-usage-chart">
        <div className="chart-error">
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!chartData) {
    return (
      <div className="app-usage-chart">
        <div className="chart-no-data">
          <span>No data available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="app-usage-chart">
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  )
}

AppUsageChart.propTypes = {}

export default AppUsageChart